import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import { type Subscription } from "@/types/subscription";

/**
 * Subscription validation and filtering utilities
 */

/**
 * Checks if a subscription is unscheduled pay-as-you-go
 * (no fixed schedule, no nextPayment)
 */
export function isUnscheduledPayAsYouGo(subscription: Subscription): boolean {
  return (
    (subscription as any).billingType === "pay_as_you_go" &&
    (!subscription.frequency || !subscription.nextPayment)
  );
}

/**
 * Checks if a database subscription is unscheduled
 * (no billing cycle or next payment date)
 */
export function isUnscheduledDatabaseSubscription(
  subscription: DatabaseSubscription,
): boolean {
  return !subscription.next_payment_date || !subscription.billing_cycle;
}

/**
 * Validates if a subscription should be included in payment status calculations
 */
export function shouldIncludeInPaymentCalculations(
  subscription: Subscription,
): boolean {
  // Exclude unscheduled PAYG
  if (isUnscheduledPayAsYouGo(subscription)) {
    return false;
  }
  // Must have next payment date
  return !!subscription.nextPayment;
}

/**
 * Validates if a database subscription should be included in calculations
 */
export function shouldIncludeDatabaseSubscriptionInCalculations(
  subscription: DatabaseSubscription,
): boolean {
  if (subscription.status !== "active") {
    return false;
  }
  return !isUnscheduledDatabaseSubscription(subscription);
}

/**
 * Filters subscriptions that are eligible for payment status calculations
 */
export function getEligibleSubscriptions(
  subscriptions: Subscription[],
): Subscription[] {
  return subscriptions.filter((subscription) => {
    if (subscription.status !== "active") return false;
    return shouldIncludeInPaymentCalculations(subscription);
  });
}

/**
 * Filters database subscriptions for payment calculations
 */
export function getEligibleDatabaseSubscriptions(
  subscriptions: DatabaseSubscription[],
): DatabaseSubscription[] {
  return subscriptions.filter(shouldIncludeDatabaseSubscriptionInCalculations);
}
